import React, { useState, useEffect } from "react";
import { useBar } from "../../../../context/BarContext";
import { useBarOwnerData } from "../../../../context/BarOwnerDataContext";

const BarAdminList = () => {
  const { selectedBar } = useBar();
  const { fetchBarAdmins, getBarAdmins } = useBarOwnerData();
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Get cached data
  const { adminsByBar, loading } = getBarAdmins();

  // Fetch admins if cache is empty
  useEffect(() => {
    const cached = getBarAdmins();
    if (!cached.adminsByBar.length && !cached.loading) {
      fetchBarAdmins();
    }
  }, [selectedBar, fetchBarAdmins, getBarAdmins]);

  const handleRemoveAdmin = async (barId, userId) => {
    try {
      const response = await fetch(`/api/bars/${barId}/admins/${userId}`, {
        method: "DELETE",
        credentials: "include",
      });

      const data = await response.json();

      if (data.success) {
        setSuccessMessage("Admin removed successfully");
        setShowRemoveConfirm(null);
        // Refresh the list
        fetchBarAdmins();
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        alert(data.error || "Failed to remove admin");
      }
    } catch (err) {
      console.error("Error removing admin:", err);
      alert("Failed to remove admin");
    }
  };

  if (loading) {
    return (
      <div className="admin-list-section">
        <div className="loading-message">Loading admins...</div>
      </div>
    );
  }

  // Find admins for selected bar
  const currentBarAdmins = adminsByBar.find(
    (barGroup) => barGroup.bar_id === selectedBar?.bar_id
  );

  // If no selected bar or no admins, show empty state
  if (
    !selectedBar ||
    !currentBarAdmins ||
    currentBarAdmins.admins.length === 0
  ) {
    return (
      <div className="admin-list-section">
        {successMessage && (
          <div
            className="alert-success"
            style={{
              padding: "10px",
              marginBottom: "20px",
              backgroundColor: "#d1f2eb",
              color: "#065f46",
              border: "1px solid #065f46",
              borderRadius: "5px",
            }}
          >
            {successMessage}
          </div>
        )}

        <div className="no-admins-message">
          <span className="no-data-icon">👥</span>
          <h3>No Admins Yet</h3>
          <p>
            {!selectedBar
              ? "Select a bar to view admins"
              : "No admins have been added to this bar yet"}
          </p>
        </div>

        {/* Remove Confirmation Modal */}
        {showRemoveConfirm && (
          <div
            className="bar-management-modal-overlay"
            onClick={() => setShowRemoveConfirm(null)}
          >
            <div
              className="bar-management-modal-container"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bar-management-modal-header">
                <h3 className="modal-title">Remove Admin</h3>
                <button
                  className="modal-close-btn"
                  onClick={() => setShowRemoveConfirm(null)}
                >
                  ×
                </button>
              </div>
              <div className="bar-management-modal-content">
                <p>
                  Are you sure you want to remove{" "}
                  <strong>{showRemoveConfirm.adminName}</strong> as an admin
                  from <strong>{showRemoveConfirm.barName}</strong>?
                </p>
                <p>They will immediately lose access to manage this bar.</p>
                <div className="modal-actions">
                  <button
                    className="cancel-btn"
                    onClick={() => setShowRemoveConfirm(null)}
                  >
                    Cancel
                  </button>
                  <button
                    className="danger-btn"
                    onClick={() =>
                      handleRemoveAdmin(
                        showRemoveConfirm.barId,
                        showRemoveConfirm.userId
                      )
                    }
                  >
                    Yes, Remove Admin
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
      <div className="admin-list-section">
        {successMessage && (
          <div
            className="alert-success"
            style={{
              padding: "10px",
              marginBottom: "20px",
              backgroundColor: "#d1f2eb",
              color: "#065f46",
              border: "1px solid #065f46",
              borderRadius: "5px",
            }}
          >
            {successMessage}
          </div>
        )}

        <div className="admin-bar-group">
          <div className="admin-bar-header">
            <h3>{currentBarAdmins.bar_name}</h3>
            <span className="admin-count">
              {currentBarAdmins.admins.length}{" "}
              {currentBarAdmins.admins.length === 1 ? "Admin" : "Admins"}
            </span>
          </div>

        <div className="admins-list">
          {currentBarAdmins.admins.map((admin) => (
            <div key={admin.user_id} className="admin-item">
              <div className="admin-avatar">
                {admin.name.charAt(0).toUpperCase()}
              </div>

              <div className="admin-info">
                <h4 className="admin-name">{admin.name}</h4>
                <p className="admin-email">{admin.email}</p>
                <p className="admin-date">
                  Added: {new Date(admin.added_date).toLocaleDateString()}
                </p>
              </div>

              <div className="admin-actions">
                <button
                  className="danger-btn"
                  onClick={() =>
                    setShowRemoveConfirm({
                      barId: currentBarAdmins.bar_id,
                      userId: admin.user_id,
                      adminName: admin.name,
                      barName: currentBarAdmins.bar_name,
                    })
                  }
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Remove Confirmation Modal */}
      {showRemoveConfirm && (
        <div
          className="bar-management-modal-overlay"
          onClick={() => setShowRemoveConfirm(null)}
        >
          <div
            className="bar-management-modal-container"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bar-management-modal-header">
              <h3 className="modal-title">Remove Admin</h3>
              <button
                className="modal-close-btn"
                onClick={() => setShowRemoveConfirm(null)}
              >
                ×
              </button>
            </div>
            <div className="bar-management-modal-content">
              <p>
                Are you sure you want to remove{" "}
                <strong>{showRemoveConfirm.adminName}</strong> as an admin from{" "}
                <strong>{showRemoveConfirm.barName}</strong>?
              </p>
              <p>They will immediately lose access to manage this bar.</p>
              <div className="modal-actions">
                <button
                  className="cancel-btn"
                  onClick={() => setShowRemoveConfirm(null)}
                >
                  Cancel
                </button>
                <button
                  className="danger-btn"
                  onClick={() =>
                    handleRemoveAdmin(
                      showRemoveConfirm.barId,
                      showRemoveConfirm.userId
                    )
                  }
                >
                  Yes, Remove Admin
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BarAdminList;
